import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PIECES, COLORS, PLOT, plotKey, footprint, topOf } from '../../shared/content/pieces.js';
import { el, clear } from '../ui/dom.js';

const WOOD = '#7a5230';
const GLASS = '#2f3d4f';
const LEAF = '#2f7d3a';
const FRAME = '#d9d4ca';
const HOUSE_ROOFS = ['#a9aeb2', '#8a5a40', '#c9ced2', '#8e3b30', '#9a6a4c', '#3f6f9e'];

function colored(geometry, hex) {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  const c = new THREE.Color(hex);
  const n = g.attributes.position.count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

// A box whose base sits at height y (not its centre).
const box = (w, h, d, hex, x = 0, y = 0, z = 0) => colored(new THREE.BoxGeometry(w, h, d).translate(x, y + h / 2, z), hex);

// A ramp: full height at the back (-z), down to nothing at the front (+z).
function wedge(w, h, d, hex) {
  const x = w / 2;
  const z = d / 2;
  const v = [
    // slope
    -x, 0, z, x, 0, z, x, h, -z, -x, 0, z, x, h, -z, -x, h, -z,
    // back
    x, 0, -z, -x, 0, -z, -x, h, -z, x, 0, -z, -x, h, -z, x, h, -z,
    // sides
    -x, 0, -z, -x, 0, z, -x, h, -z, x, 0, z, x, 0, -z, x, h, -z,
    // underside
    -x, 0, -z, x, 0, -z, x, 0, z, -x, 0, -z, x, 0, z, -x, 0, z,
  ];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((v.length / 3) * 2), 2));
  g.computeVertexNormals();
  return colored(g, hex);
}

// The shapes that make up each piece, with its base centre at the origin.
function shapeOf(id, hex) {
  switch (id) {
    case 'window':
      return [box(4, 1, 0.5, hex), box(4, 0.6, 0.5, hex, 0, 2.4), box(1, 1.4, 0.5, hex, -1.5, 1), box(1, 1.4, 0.5, hex, 1.5, 1), box(2, 1.4, 0.12, GLASS, 0, 1)];
    case 'door':
      return [box(1.4, 3, 0.5, hex, -1.3), box(1.4, 3, 0.5, hex, 1.3), box(1.2, 0.8, 0.5, hex, 0, 2.2)];
    case 'roof':
      return [wedge(4, 1.5, 4, hex)];
    case 'fence':
      return [box(0.2, 1, 0.2, WOOD, -1.9), box(0.2, 1, 0.2, WOOD), box(0.2, 1, 0.2, WOOD, 1.9), box(4, 0.14, 0.1, hex, 0, 0.7), box(4, 0.14, 0.1, hex, 0, 0.3)];
    case 'stall':
      return [box(0.14, 2.2, 0.14, WOOD, -1.4, 0, -0.9), box(0.14, 2.2, 0.14, WOOD, 1.4, 0, -0.9), box(0.14, 2.2, 0.14, WOOD, -1.4, 0, 0.9), box(0.14, 2.2, 0.14, WOOD, 1.4, 0, 0.9), box(3.3, 0.14, 2.3, hex, 0, 2.2), box(2.8, 0.1, 1, WOOD, 0, 0.85, -0.3)];
    case 'house': {
      // zinc of a different age and paint from house to house
      const roof = HOUSE_ROOFS[Math.max(0, COLORS.indexOf(hex)) % HOUSE_ROOFS.length];
      const pane = (x, z, alongX) => [box(alongX ? 1.6 : 0.1, 1.3, alongX ? 0.1 : 1.6, FRAME, x, 1, z), box(alongX ? 1.3 : 0.14, 1, alongX ? 0.14 : 1.3, GLASS, x, 1.15, z)];
      return [
        box(8, 3, 7, hex),
        // the plinth reaches below the floor so the house sits properly on sloping ground
        box(8.3, 1.8, 7.3, '#8d8a86', 0, -1.8),
        box(1.3, 2.2, 0.1, FRAME, 0, 0, 3.5),
        box(1.05, 2.05, 0.14, WOOD, 0, 0, 3.5),
        ...pane(-2.5, 3.5, true),
        ...pane(2.5, 3.5, true),
        ...pane(-2.2, -3.5, true),
        ...pane(2.2, -3.5, true),
        ...pane(4, 0.4, false),
        ...pane(-4, 0.4, false),
        colored(new THREE.ConeGeometry(6.3, 2.2, 4).rotateY(Math.PI / 4).scale(1, 1, 0.9).translate(0, 4.1, 0), roof),
      ];
    }
    case 'tree':
      return [colored(new THREE.CylinderGeometry(0.22, 0.32, 3, 6).translate(0, 1.5, 0), WOOD), colored(new THREE.IcosahedronGeometry(2.2, 0).translate(0, 4.2, 0), LEAF)];
    case 'lamp':
      return [box(0.16, 3.8, 0.16, '#3a3a3e'), box(0.7, 0.14, 0.3, '#3a3a3e', 0.3, 3.8), box(0.34, 0.1, 0.24, '#fff4c0', 0.45, 3.7)];
    default: {
      const [w, h, d] = PIECES.find((p) => p.id === id).size;
      return [box(w, h, d, hex)];
    }
  }
}

function partGeometry(part) {
  const g = mergeGeometries(shapeOf(PIECES[part[0]].id, COLORS[part[5]]));
  g.rotateY((part[4] * Math.PI) / 2);
  g.translate(part[1] / 2, part[2] / 2, part[3] / 2);
  return g;
}

// Build mode: plots of land and the parts players put on them.
export class Builder {
  constructor(scene, map, net, hud, root) {
    this.scene = scene;
    this.map = map;
    this.net = net;
    this.hud = hud;
    this.plots = new Map(); // key -> { name, mine, parts, mesh }
    this.active = false;
    this.piece = 0;
    this.color = 0;
    this.turn = 0;
    this.target = null;
    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });
    this.ghostMaterial = new THREE.MeshBasicMaterial({ color: 0xf2b705, transparent: true, opacity: 0.5, depthWrite: false });
    this.ghost = null;
    this.ghostKey = '';
    this.askedAt = { x: Infinity, z: Infinity, t: 0 };
    this.bar = el('div', { id: 'buildbar', hidden: true });
    root.appendChild(this.bar);
    this.ray = new THREE.Vector3();

    net.on('plots', (m) => this.setPlots(m.list));
    this.physics = null; // set by the game when ?physics=1
    net.on('placed', (m) => this.onPlaced(m));
    net.on('removed', (m) => this.onRemoved(m));
    net.on('refused', (m) => hud.toast(m.why));
  }

  // ---- what is built ----

  setPlots(list) {
    const seen = new Set();
    for (const p of list) {
      seen.add(p.key);
      let plot = this.plots.get(p.key);
      if (!plot) this.plots.set(p.key, (plot = { key: p.key, name: '', mine: false, parts: [], mesh: null }));
      plot.name = p.name;
      plot.mine = p.mine;
      // the list comes every few seconds: only what has really changed is drawn again
      const now = JSON.stringify(p.parts);
      if (now === plot.drawn) continue;
      plot.drawn = now;
      plot.parts = p.parts;
      this.rebuild(plot);
    }
    for (const [key, plot] of this.plots) {
      if (seen.has(key)) continue;
      if (plot.mesh) this.dropMesh(plot);
      this.plots.delete(key);
    }
  }

  onPlaced(m) {
    let plot = this.plots.get(m.key);
    if (!plot) this.plots.set(m.key, (plot = { key: m.key, name: m.name, mine: m.mine, parts: [], mesh: null }));
    plot.parts.push(m.part);
    plot.drawn = JSON.stringify(plot.parts);
    this.rebuild(plot);
    if (m.mine && plot.parts.length === 1) this.hud.toast('This plot is now yours. Only you can build on it.');
  }

  onRemoved(m) {
    const plot = this.plots.get(m.key);
    if (!plot) return;
    const k = plot.parts.findIndex((p) => p.every((v, i) => v === m.part[i]));
    if (k >= 0) plot.parts.splice(k, 1);
    plot.drawn = JSON.stringify(plot.parts);
    if (plot.parts.length === 0) {
      this.dropMesh(plot);
      this.plots.delete(m.key);
    } else this.rebuild(plot);
  }

  dropMesh(plot) {
    this.scene.remove(plot.mesh);
    plot.mesh.geometry.dispose();
    plot.mesh = null;
    if (this.physics) this.physics.dropParts(plot.key);
  }

  rebuild(plot) {
    if (plot.mesh) this.dropMesh(plot);
    if (plot.parts.length === 0) return;
    if (this.physics) this.physics.setParts(plot.key, plot.parts);
    const mesh = new THREE.Mesh(mergeGeometries(plot.parts.map(partGeometry)), this.material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    plot.mesh = mesh;
    this.scene.add(mesh);
  }

  // Parts whose ground rectangle covers a point.
  partsAt(x, z, fn) {
    const px = Math.floor(x / PLOT);
    const pz = Math.floor(z / PLOT);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const plot = this.plots.get(`${px + dx}_${pz + dz}`);
        if (!plot) continue;
        for (const part of plot.parts) {
          const f = footprint(part);
          if (x >= f.x0 && x <= f.x1 && z >= f.z0 && z <= f.z1) fn(part, plot);
        }
      }
    }
  }

  // The height of whatever is at a point: the ground or the top of the highest part.
  surface(x, z) {
    let y = this.map.heightmap.heightAt(x, z);
    this.partsAt(x, z, (part) => {
      const t = topOf(part);
      if (t > y) y = t;
    });
    return y;
  }

  // Where someone standing with feet at feetY can stand: the ground, or a part
  // low enough to step onto.
  groundAt(x, z, feetY) {
    let y = this.map.heightmap.heightAt(x, z);
    this.partsAt(x, z, (part) => {
      if (PIECES[part[0]].id === 'roof' || PIECES[part[0]].id === 'tree') return;
      const t = topOf(part);
      if (t > y && t <= feetY + 0.65) y = t;
    });
    return y;
  }

  // Adds the parts that block someone at (x, z, feetY) to a collider list.
  collect(x, z, feetY, list) {
    const px = Math.floor(x / PLOT);
    const pz = Math.floor(z / PLOT);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const plot = this.plots.get(`${px + dx}_${pz + dz}`);
        if (!plot) continue;
        for (const part of plot.parts) {
          if (PIECES[part[0]].passable) continue;
          const top = topOf(part);
          if (top <= feetY + 0.65 || part[2] / 2 >= feetY + 1.8) continue;
          const f = footprint(part);
          if (x < f.x0 - 3 || x > f.x1 + 3 || z < f.z0 - 3 || z > f.z1 + 3) continue;
          list.push({ p: [f.x0, f.z0, f.x1, f.z0, f.x1, f.z1, f.x0, f.z1], minX: f.x0, minZ: f.z0, maxX: f.x1, maxZ: f.z1, top });
        }
      }
    }
    return list;
  }

  // ---- building ----

  toggle() {
    this.active = !this.active;
    this.bar.hidden = !this.active;
    if (this.ghost) this.ghost.visible = this.active;
    if (this.active) this.showBar();
    return this.active;
  }

  showBar() {
    clear(this.bar);
    PIECES.forEach((p, k) => {
      const key = k < 9 ? String(k + 1) : k === 9 ? '0' : '';
      this.bar.append(el(`span${k === this.piece ? '.on' : ''}`, null, key ? el('b', null, key) : null, p.name));
    });
    this.bar.append(el('i', { style: `background:${COLORS[this.color]}`, title: 'Colour' }));
  }

  // Call every frame while building. camera: the game camera. input: key state.
  update(camera, input, x, z, t) {
    if (this.net.online && (Math.abs(x - this.askedAt.x) > 120 || Math.abs(z - this.askedAt.z) > 120 || t - this.askedAt.t > 8000)) {
      this.askedAt = { x, z, t };
      this.net.send({ t: 'plots', x, z });
    }
    if (!this.active) return;

    for (let k = 0; k < PIECES.length && k < 10; k++) {
      if (input.hit(k === 9 ? '0' : String(k + 1))) this.piece = k;
    }
    if (input.hit(']')) this.piece = (this.piece + 1) % PIECES.length;
    if (input.hit('[')) this.piece = (this.piece + PIECES.length - 1) % PIECES.length;
    if (input.hit('c')) this.color = (this.color + 1) % COLORS.length;
    if (input.hit('r')) this.turn = (this.turn + 1) % 4;
    const key = `${this.piece}|${this.color}|${this.turn}`;
    if (key !== this.ghostKey) {
      this.ghostKey = key;
      this.showBar();
      if (this.ghost) {
        this.scene.remove(this.ghost);
        this.ghost.geometry.dispose();
      }
      const g = mergeGeometries(shapeOf(PIECES[this.piece].id, COLORS[this.color]));
      g.rotateY((this.turn * Math.PI) / 2);
      this.ghost = new THREE.Mesh(g, this.ghostMaterial);
      this.scene.add(this.ghost);
    }

    // walk a ray out from the middle of the view until it meets the ground or a part
    camera.getWorldDirection(this.ray);
    const o = camera.position;
    this.target = null;
    for (let d = 2; d < 60; d += 0.4) {
      const px = o.x + this.ray.x * d;
      const py = o.y + this.ray.y * d;
      const pz = o.z + this.ray.z * d;
      if (py > this.surface(px, pz)) continue;
      const sx = Math.round(px * 2) / 2;
      const sz = Math.round(pz * 2) / 2;
      const sy = Math.ceil(this.surface(sx, sz) * 2 - 0.2) / 2;
      this.target = [this.piece, Math.round(sx * 2), Math.round(sy * 2), Math.round(sz * 2), this.turn, this.color];
      this.ghost.position.set(sx, sy, sz);
      break;
    }
    this.ghost.visible = !!this.target;
    if (!this.target) return;

    if (input.clicked) {
      const plot = this.plots.get(plotKey(this.target[1] / 2, this.target[3] / 2));
      if (plot && !plot.mine) this.hud.toast(`This plot belongs to ${plot.name}.`);
      else if (!this.net.online) this.hud.toast('Building needs the server. It is not connected.');
      else this.net.send({ t: 'place', part: this.target });
    }
    if (input.hit('x')) {
      // take away the highest of my parts under the pointer
      let pick = null;
      this.partsAt(this.target[1] / 2, this.target[3] / 2, (part, plot) => {
        if (plot.mine && (!pick || topOf(part) > topOf(pick))) pick = part;
      });
      if (pick) this.net.send({ t: 'remove', part: pick });
      else this.hud.toast('Nothing of yours to remove here');
    }
  }
}
